import { Organization, OrganizationUpdateData } from "@/types/organization";

export async function createOrganization(
  name: string,
  logo?: string,
): Promise<Organization> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch("/api/organization", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name, logo }),
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create organization");
    }

    const { organization } = await response.json();
    return organization;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function deleteOrganization(
  organizationId: string,
): Promise<void> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch(`/api/organization`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
      body: JSON.stringify({ id: organizationId }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to delete organization");
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function getOrganization(
  organizationId: string,
  forceRefresh = false,
): Promise<Organization | null> {
  const url = forceRefresh
    ? `/api/organization?orgId=${organizationId}&_=${Date.now()}`
    : `/api/organization?orgId=${organizationId}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    cache: forceRefresh ? "no-store" : "force-cache",
    next: forceRefresh
      ? undefined
      : {
          tags: ["organization"],
          revalidate: 3600,
        },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to get organization");
  }

  try {
    const { organization } = await response.json();
    return organization || null;
  } catch (error: any) {
    throw new Error(error.message || "Failed to get organization");
  }
}

export async function updateOrganization(
  orgData: OrganizationUpdateData,
): Promise<Organization> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch("/api/organization", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(orgData),
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to update organization");
    }

    try {
      const { organization } = await response.json();
      return organization;
    } catch (error: any) {
      throw new Error(error.message || "Failed to update organization");
    }
  } finally {
    clearTimeout(timeoutId);
  }
}
